import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStaff } from '@/stores/staff';
import { useFlags, useOrg, useRole } from '@/stores/identity';
import { useLocations } from '@/stores/locations';
import { StaffStatusBadge } from '@/components/StaffStatusBadge';
import { ManagerBadge } from '@/components/ManagerBadge';
import { listManagerships, type Managership } from '@/lib/staff';
import { JOB_TYPES, EMPLOYMENT_TYPES } from '@/lib/types';

/**
 * Staff directory. Shows all staff the caller can see (RLS-scoped via
 * app_user_visible_staff()). Owners and managers can navigate into a row;
 * staff role doesn't see this page (route is gated at App.tsx).
 */
export default function StaffList() {
  const status = useStaff((s) => s.status);
  const items = useStaff((s) => s.items);
  const error = useStaff((s) => s.error);
  const load = useStaff((s) => s.load);

  const locations = useLocations((s) => s.items);
  const locStatus = useLocations((s) => s.status);
  const loadLocations = useLocations((s) => s.load);

  const role = useRole();
  const myOrg = useOrg();
  const myFlags = useFlags();
  const isPlatformAdmin = !!myFlags?.is_platform_admin;
  const isOwner = role === 'owner';

  useEffect(() => {
    if (status === 'idle') void load();
    if (locStatus === 'idle') void loadLocations();
  }, [status, locStatus, load, loadLocations]);

  // Managerships: which profile is manager at which location. Used for the
  // "Manager · Queen St" badge on each row. RLS scopes this to the caller's
  // org so a single fetch covers the whole list.
  const [managerships, setManagerships] = useState<Managership[]>([]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await listManagerships();
        if (!cancelled) setManagerships(rows);
      } catch {
        // Silent: badge is informational; the page still renders without it.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">Staff</h1>
            <p className="text-sm text-muted-foreground">
              People employed at your organization. Add staff first, then send
              an invite to give them app access.
            </p>
          </div>
          {isOwner && (
            <Link
              to="/staff/new"
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              + Add staff
            </Link>
          )}
        </div>

        {status === 'loading' && (
          <p className="text-sm text-muted-foreground">Loading staff...</p>
        )}
        {status === 'error' && (
          <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}
        {status === 'loaded' && items.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No staff yet. {isOwner && 'Click "+ Add staff" to add the first.'}
          </div>
        )}

        {status === 'loaded' && items.length > 0 && (
          <ul className="space-y-2">
            {items.map((s) => {
              const primaryLoc = locations.find(
                (l) => l.id === s.primary_location_id
              );
              const jobLabel =
                JOB_TYPES.find((j) => j.value === s.job_type)?.label ??
                s.job_type;
              const empLabel =
                EMPLOYMENT_TYPES.find((e) => e.value === s.employment_type)
                  ?.label ?? s.employment_type;

              return (
                <li
                  key={s.id}
                  className="rounded-lg border border-border bg-card p-4 hover:bg-muted/30"
                >
                  <Link
                    to={`/staff/${s.id}`}
                    className="flex items-center justify-between gap-4"
                  >
                    <div className="space-y-0.5 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-medium truncate">
                          {s.display_name}
                        </h3>
                        <StaffStatusBadge staff={s} />
                        {(() => {
                          const m = s.profile_id
                            ? managerships.find((mm) => mm.user_id === s.profile_id)
                            : undefined;
                          return m ? (
                            <ManagerBadge locationName={m.location_name} />
                          ) : null;
                        })()}
                        {isPlatformAdmin && myOrg && s.organization_id !== myOrg.id && (
                          <span
                            className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
                            title="This staff belongs to another organization. You can view it as a platform admin, but cannot make changes."
                          >
                            Other org · read-only
                          </span>
                        )}
                        {!s.profile_id && (
                          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                            No app access
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {jobLabel} · {empLabel}
                        {primaryLoc && ` · ${primaryLoc.name}`}
                      </p>
                    </div>
                    {s.email && (
                      <span className="text-xs text-muted-foreground shrink-0 truncate max-w-[12rem]">
                        {s.email}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
